// The moderator's test queue: pending tests to correct, corrected tests by
// contract stage, final-validated tests, and the nav between them.
import { useEffect, useMemo, useState } from "react";
import { testsApi } from "@/api/tests.api";
import { useLanguages } from "@/hooks/use-languages";
import { ContractStatus } from "@/lib/types/contracts";
import type { ModeratorGetPaidNavItem, ModeratorGetPaidTab } from "@/components/get-paid";
import { sortTestsByScore } from "../helpers";
import {
  NAV_TAB_STATUS,
  SECTION_STATUS,
  correctedForTab,
  moderatorNavTabFor,
  normalizeValidatedSubmission,
  pendingQueue,
  sectionCount,
  visibleCorrected,
  visiblePending,
} from "../logic/moderation-logic";
import type { GetPaidBase } from "./get-paid-context";

export function useModeratorTests({ t, user, toast, searchParams }: GetPaidBase & { searchParams: URLSearchParams }) {
  const { activeLanguages } = useLanguages({ scope: "all" });
  const [moderationLang, setModerationLang] = useState<string>("");

  useEffect(() => {
    if (moderationLang === "" && activeLanguages.length > 0) {
      setModerationLang(activeLanguages[0].code);
    }
  }, [activeLanguages, moderationLang]);

  const [pendingTests, setPendingTests] = useState<any[]>([]);
  const [correctedTests, setCorrectedTests] = useState<any[]>([]);
  const [validatedTests, setValidatedTests] = useState<any[]>([]);
  // Shared with the admin's eligible-users list, as before the split.
  const [loadingTests, setLoadingTests] = useState(false);

  const requestedModeratorSection = searchParams.get("section");
  const [activeModTab, setActiveModTab] = useState<"PENDING" | "VALIDATED">(requestedModeratorSection === "pending" ? "PENDING" : "VALIDATED");
  const [activeCorrectedContractTab, setActiveCorrectedContractTab] = useState<ContractStatus>(SECTION_STATUS[requestedModeratorSection ?? ""] ?? ContractStatus.DRAFT);
  useEffect(() => {
    if (requestedModeratorSection === "pending") setActiveModTab("PENDING");
    else if (requestedModeratorSection && SECTION_STATUS[requestedModeratorSection]) {
      setActiveModTab("VALIDATED");
      setActiveCorrectedContractTab(SECTION_STATUS[requestedModeratorSection]);
    }
  }, [requestedModeratorSection]);

  const visiblePendingTests = useMemo(() => visiblePending(pendingTests, moderationLang), [pendingTests, moderationLang]);

  const visibleCorrectedTests = useMemo(
    () => sortTestsByScore(visibleCorrected(correctedTests, validatedTests, moderationLang, activeCorrectedContractTab)),
    [activeCorrectedContractTab, correctedTests, validatedTests, moderationLang],
  );

  const correctedContractTabs = useMemo(
    () => [
      { value: ContractStatus.DRAFT, label: t.getPaid.contractToSend },
      { value: ContractStatus.SENT, label: t.getPaid.contractSentLabel },
      { value: ContractStatus.VIEWED, label: t.getPaid.contractViewed },
      { value: ContractStatus.SIGNED, label: t.getPaid.readyForFinalValidation },
      { value: ContractStatus.FINAL_VALIDATED, label: t.getPaid.alreadyFinalized },
    ],
    [t],
  );

  const moderatorNavTab = useMemo<ModeratorGetPaidTab>(
    () => moderatorNavTabFor(activeModTab, activeCorrectedContractTab),
    [activeModTab, activeCorrectedContractTab],
  );

  const moderatorNavItems = useMemo<ModeratorGetPaidNavItem[]>(() => {
    const lists = { pendingTests, correctedTests, validatedTests };
    const count = (status: ContractStatus | "pending") => sectionCount(status, lists, moderationLang);
    return [
      { value: "pending", label: t.getPaid.toCorrect, description: t.getPaid.pendingCorrectionDesc, icon: "fileText", count: count("pending") },
      { value: "draft", label: t.getPaid.contractToSend, description: t.getPaid.contractsToSendDesc, icon: "edit", count: count(ContractStatus.DRAFT) },
      { value: "sent", label: t.getPaid.contractSentLabel, description: t.getPaid.contractsSentDesc, icon: "send", count: count(ContractStatus.SENT) },
      { value: "viewed", label: t.getPaid.contractViewed, description: t.getPaid.contractsViewedDesc, icon: "eye", count: count(ContractStatus.VIEWED) },
      { value: "signed", label: t.getPaid.readyForFinalValidation, description: t.getPaid.contractsReadyFinalDesc, icon: "checkCircle", count: count(ContractStatus.SIGNED) },
      { value: "final_validated", label: t.getPaid.alreadyFinalized, description: t.getPaid.contractsFinalizedDesc, icon: "checkCircle2", count: count(ContractStatus.FINAL_VALIDATED) },
    ];
  }, [t, pendingTests, correctedTests, validatedTests, moderationLang]);

  const handleModeratorNavChange = (value: ModeratorGetPaidTab) => {
    if (value === "pending") {
      setActiveModTab("PENDING");
      return;
    }
    setActiveModTab("VALIDATED");
    setActiveCorrectedContractTab(NAV_TAB_STATUS[value]);
  };

  const loadPendingTests = async () => {
    setLoadingTests(true);
    try {
      const res = await testsApi.getPendingSubmissions(1, 50);
      const items: any[] = Array.isArray(res?.items) ? res.items : [];
      if (!items.length) {
        setPendingTests([]);
        return;
      }
      setPendingTests(pendingQueue(items) as any);
      setCorrectedTests([]);
    } catch {
      toast({ title: t.error, description: t.getPaid.requestsLoadError, variant: "destructive" });
    } finally {
      setLoadingTests(false);
    }
  };

  const loadValidatedTests = async () => {
    setLoadingTests(true);
    try {
      const res = await testsApi.getSubmissionsReadyForFinalValidation(1, 200, ContractStatus.FINAL_VALIDATED);
      const items: any[] = Array.isArray(res?.items) ? res.items : [];
      if (!items.length) {
        setValidatedTests([]);
        return;
      }
      setValidatedTests(sortTestsByScore(items.map((it) => normalizeValidatedSubmission(it, t.getPaid, user)) as any));
    } catch {
      toast({ title: t.error, description: t.getPaid.correctedTestsLoadError, variant: "destructive" });
    } finally {
      setLoadingTests(false);
    }
  };

  const loadCorrectedTests = async (contractStatus = activeCorrectedContractTab) => {
    try {
      setLoadingTests(true);
      setCorrectedTests([]);
      const response = await testsApi.getSubmissionsReadyForFinalValidation(1, 200, contractStatus);
      const items = Array.isArray((response as any)?.items) ? (response as any).items : [];
      const nextItems = sortTestsByScore(correctedForTab(items, t.getPaid, moderationLang, contractStatus));
      if (contractStatus === ContractStatus.FINAL_VALIDATED) {
        setValidatedTests(nextItems);
      } else {
        setCorrectedTests(nextItems);
      }
    } catch {
      if (contractStatus === ContractStatus.FINAL_VALIDATED) {
        setValidatedTests([]);
      } else {
        setCorrectedTests([]);
      }
      toast({ title: t.error, description: t.getPaid.correctedTestsLoadError, variant: "destructive" });
    } finally {
      setLoadingTests(false);
    }
  };

  return {
    moderationLang, setModerationLang,
    pendingTests, setPendingTests, correctedTests, setCorrectedTests, validatedTests,
    loadingTests, setLoadingTests,
    activeModTab, setActiveModTab, activeCorrectedContractTab,
    visiblePendingTests, visibleCorrectedTests, correctedContractTabs,
    moderatorNavTab, moderatorNavItems, handleModeratorNavChange,
    loadPendingTests, loadValidatedTests, loadCorrectedTests,
  };
}
